import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { portfolioItems } from './OurEvents';
export function EventDetail({
  openQuote
}: {
  openQuote: (item?: string) => void;
}) {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);
  const currentIndex = portfolioItems.findIndex(item => item.id === Number(id));
  if (currentIndex === -1) {
    return <div className="min-h-screen pt-32 flex flex-col items-center justify-center text-center px-6" data-id="element-541">
        <h2 className="text-3xl font-serif mb-4" data-id="element-542">Event Not Found</h2>
        <Link to="/events" className="text-brand-gold hover:text-brand-black transition-colors font-bold uppercase tracking-widest text-sm" data-id="element-543">
          Return to Events
        </Link>
      </div>;
  }
  const event = portfolioItems[currentIndex];
  const prevEvent = portfolioItems[currentIndex === 0 ? portfolioItems.length - 1 : currentIndex - 1];
  const nextEvent = portfolioItems[currentIndex === portfolioItems.length - 1 ? 0 : currentIndex + 1];
  return <div className="bg-white min-h-screen pt-20" data-id="element-544">
      {/* Hero Section */}
      <section className="relative h-[60vh] md:h-[70vh] w-full overflow-hidden bg-brand-black" data-id="element-545">
        {event.mediaType === 'video' ? <video autoPlay muted loop playsInline poster={event.poster} className="w-full h-full object-cover opacity-60" data-id="element-546">
            <source src={event.src} type="video/mp4" data-id="element-547" />
          </video> : <img src={event.src} alt={event.title} className="w-full h-full object-cover opacity-60" data-id="element-548" />}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-transparent to-transparent" data-id="element-549" />

        <div className="absolute inset-0 flex items-end pb-16 md:pb-24 px-6 max-w-7xl mx-auto" data-id="element-550">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-551">
            <Link to="/events" className="inline-flex items-center gap-2 text-white/70 hover:text-brand-gold transition-colors text-sm font-bold uppercase tracking-widest mb-6" data-id="element-552">
              <ArrowLeft size={16} data-id="element-553" /> Back to Portfolio
            </Link>
            <span className="text-brand-gold font-bold tracking-[0.3em] uppercase text-xs mb-3 block" data-id="element-554">
              {event.category}
            </span>
            <h1 className="text-white text-4xl sm:text-5xl md:text-7xl font-serif max-w-4xl" data-id="element-555">
              {event.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Details & Gallery */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-6" data-id="element-556">
        <div className="grid lg:grid-cols-3 gap-16" data-id="element-557">
          {/* Description */}
          <div className="lg:col-span-1" data-id="element-558">
            <h3 className="text-2xl font-serif mb-6 text-brand-black border-b border-gray-100 pb-4" data-id="element-559">
              About This Event
            </h3>
            <p className="text-gray-600 text-lg leading-relaxed mb-8" data-id="element-560">
              {event.description}
            </p>
            <button onClick={() => openQuote(event.category)} className="w-full bg-brand-gold text-white py-4 rounded-sm font-bold uppercase tracking-widest hover:bg-brand-black transition-all shadow-md" data-id="element-561">
              Book a Similar Event
            </button>
          </div>

          {/* Gallery Grid */}
          <div className="lg:col-span-2" data-id="element-562">
            <div className="grid sm:grid-cols-2 gap-6" data-id="element-563">
              {event.gallery.map((imgSrc, idx) => <div key={idx} className="aspect-square overflow-hidden rounded-sm bg-gray-100 shadow-sm" data-id="element-564">
                  <img src={imgSrc} alt={`${event.title} Gallery ${idx + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" data-id="element-565" />
                </div>)}
              {/* Show the main media again if it's an image, or just let the gallery handle it */}
              {event.mediaType === 'image' && <div className="aspect-square overflow-hidden rounded-sm bg-gray-100 shadow-sm sm:col-span-2" data-id="element-566">
                  <img src={event.src} alt={event.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" data-id="element-567" />
                </div>}
            </div>
          </div>
        </div>
      </section>

      {/* Previous / Next Navigation */}
      <section className="border-t border-gray-100" data-id="element-568">
        <div className="grid grid-cols-2 divide-x divide-gray-100" data-id="element-569">
          <Link to={`/events/${prevEvent.id}`} className="group flex flex-col p-8 md:p-16 hover:bg-gray-50 transition-colors text-left" data-id="element-570">
            <span className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-2" data-id="element-571">
              <ChevronLeft size={16} className="transform group-hover:-translate-x-1 transition-transform" data-id="element-572" />{' '}
              Previous Event
            </span>
            <span className="text-xl md:text-3xl font-serif text-brand-black group-hover:text-brand-gold transition-colors line-clamp-1" data-id="element-573">
              {prevEvent.title}
            </span>
          </Link>

          <Link to={`/events/${nextEvent.id}`} className="group flex flex-col p-8 md:p-16 hover:bg-gray-50 transition-colors text-right items-end" data-id="element-574">
            <span className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-2" data-id="element-575">
              Next Event{' '}
              <ChevronRight size={16} className="transform group-hover:translate-x-1 transition-transform" data-id="element-576" />
            </span>
            <span className="text-xl md:text-3xl font-serif text-brand-black group-hover:text-brand-gold transition-colors line-clamp-1" data-id="element-577">
              {nextEvent.title}
            </span>
          </Link>
        </div>
      </section>
    </div>;
}